/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import MilestoneType from '@linc-technologies/linced-common/enums/milestone-type';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import { calculateYearLevelMilestoneDate } from '@linc-technologies/linced-common/utils/progressions';
import moment from 'moment';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { classNames } from '@ember-decorators/component';

@classNames('otj-grader')
export default class OtjSelect extends Component {
  // Services
  @service
  dates;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  milestone = null;
  otj = null;
  inline = false;
  ProgressionJudgement = ProgressionJudgement;

  // Props
  // type provides the type of progression to be reporting for.
  // Defaults to Progressions.
  type = ProgressionType.PROGRESSION;

  // milestones provides the milestones that the widget will update the overall
  // teacher judgement for.
  milestones = null;

  // currentMilestone provides the current milestone that the widget for the
  // overall teacher judgement.
  currentMilestone = null;

  // levels provides the levels that the widget will update the overall teacher
  // judgement for.
  levels = null;

  // expectations provides the raw expectations for all milestones that the
  // widget will use to update the specific overall teacher judgement for.
  expectations = null;

  // records provides the records that the widget will update the overall
  // teacher judgement for.
  records = null;

  // person provides the person that the widget will update the overall teacher
  // judgement for.
  person = null;

  previousLevel = null;

  // selectedLevel is the current progression level selected on the slider.
  selectedLevel = null;

  // deleteProgressionDialogOpen toggles the delete progression dialog.
  @tracked
  deleteProgressionDialogOpen = false;

  // progressionErrorDialogOpen toggles the create progression error dialog.
  @tracked
  progressionErrorDialogOpen = false;

  // progressionErrorCodes contains a string of error response codes from progression creation.
  @tracked
  progressionErrorCodes = '';

  @tracked
  deleteInProgress;

  // `currentRecordOverride` lets callers override the computed default; Ember < 4
  // allowed that implicitly (computed-property.override).
  currentRecordOverride = null;

  @computed('resolvedRecords.@each.createTime', 'currentRecordOverride')
  get currentRecord() {
    if (this.currentRecordOverride !== null) {
      return this.currentRecordOverride;
    }

    let resolvedRecords = this.resolvedRecords;
    if (Array.isArray(resolvedRecords) && resolvedRecords.length > 0) {
      let mostRecentRecord = sortBy(resolvedRecords, 'createTime').at(-1);
      return mostRecentRecord;
    }
    return null;
  }

  getProcessedRecords(learnerProgressions, learnerId, subjectId) {
    if (learnerProgressions && learnerProgressions[learnerId]) {
      let records = learnerProgressions[learnerId][subjectId];
      return Promise.resolve(records || []); // Always return a promise
    }

    return Promise.resolve([]); // Default to an empty array
  }

  // createProgression creates a new data point for a teacher judgement,
  // marking student progress.
  createProgression(OTJ, milestone, level) {
    let progression = {
      schoolId: this.get('session.school'),
      personId: this.person,
      assessorId: this.get('session.person'),
      milestoneId: milestone,
      dueDate: this.getDueDate(this.person, milestone),
      progressionType: this.type,
      goalSubjectId: this.subject,
      judgement: OTJ,
      levelId: level,
    };
    return this.store
      .createRecord('milestones/progression', progression)
      .save()
      .catch((err) => {
        this.progressionErrorCodes = this._getErrorStatuses(err);
        this.progressionErrorDialogOpen = true;
      })
      .finally(() => {
        this._updateRecords();
      });
  }

  _getErrorStatuses(err) {
    return err.errors
      .map((error) => {
        return error.status;
      })
      .join(',');
  }

  // getDueDate calculates when an overall teacher judgement is due.
  getDueDate(person, milestone) {
    let dueDate = moment();
    let year = dueDate.year();

    if (milestone.type !== MilestoneType.YEAR_LEVEL) {
      if (isEmpty(person.firstSchooling)) {
        // No first schooling date defined, but we need one.
        // We shouldn't get here, but if we do, just return today.
        return dueDate.format('YYYY-MM-DD');
      } else {
        // Start with the first schooling date.
        // We'll add the number of weeks or months on to it.
        dueDate = moment(person.firstSchooling);
      }
    }

    switch (milestone.type) {
      case MilestoneType.WEEKS_AT_SCHOOL:
        dueDate.add(milestone.duration, 'weeks');
        break;

      case MilestoneType.MONTHS_AT_SCHOOL:
        dueDate.add(milestone.duration, 'months');
        break;

      case MilestoneType.YEAR_LEVEL:
        year = year - person.numericCurrentYearLevel + milestone.yearLevel;
        dueDate = calculateYearLevelMilestoneDate(
          year,
          milestone,
          this.dates.rolloverMonthDate,
        );
        break;
    }

    return dueDate.format('YYYY-MM-DD');
  }

  // deleteProgression removes a single data point of student progress.
  deleteProgression() {
    this.deleteInProgress = true;
    return this.currentRecord
      .destroyRecord()
      .then(() => {
        this._updateRecords();
        this._notify();
      })
      .catch((err) => this._notify(err))
      .finally(() => (this.deleteInProgress = false));
  }

  // _notify informs the user of success or fail.
  _notify(err) {
    if (isEmpty(err)) {
      this.toaster.success(`Progression deleted`);
      return;
    }
    this.toaster.error(`Error deleting progression!`);
  }

  // _updateRecords runs the update records callback if provided, or updates
  // the records locally.
  _updateRecords() {
    if (this.updateRecords) {
      this.updateRecords();
    } else {
      let records = this.records;

      if (records && typeof records.then === 'function') {
        // Handle records as a promise
        records.then((resolved) => {
          this.set('resolvedRecords', resolved || []);
          this.notifyPropertyChange('resolvedRecords');
        });
      } else if (Array.isArray(records)) {
        // Handle records as a resolved array
        this.set('resolvedRecords', records);
        this.notifyPropertyChange('resolvedRecords');
      }
    }
  }

  @computed('session.scopes.[]')
  get isAdmin() {
    return this.permissions.meet('urn:linced:organisation:role:admin');
  }

  @action
  addOTJ(OTJ, milestone, level) {
    this.createProgression(OTJ, milestone, level);
  }

  @action
  clearOTJ() {
    this.deleteInProgress = true;
    this.deleteProgression();
  }

  // changeJudgement provides an action to change the current OTJ to the
  // selected one.
  @action
  changeJudgement(OTJ) {
    this.setJudgement(OTJ);
  }

  // confirmDelete deletes a progression.
  @action
  confirmDelete(confirmed) {
    if (confirmed) {
      this.deleteInProgress = true;
      this.deleteProgression();
    }

    this.deleteProgressionDialogOpen = false;
  }

  @action
  noOp() {}

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    let records = this.records; // Get the raw data passed to the component
    if (records && typeof records.then === 'function') {
      // Handle records as a promise
      records.then((resolved) => {
        this.set('resolvedRecords', resolved || []);
      });
    } else if (Array.isArray(records)) {
      // Handle records as a resolved array
      this.set('resolvedRecords', records || []);
    } else {
      // Handle the case where records are missing or invalid
      this.set('resolvedRecords', []);
    }
  }
}
